import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card } from "@/components/ui/card";
import { ExternalLink, FileCheck } from "lucide-react";

interface RegistrationLink {
  name: string;
  description: string;
  url: string;
}

interface Section {
  title: string;
  icon: string;
  links: RegistrationLink[];
}

const sections: Section[] = [
  {
    title: "Canada",
    icon: "🇨🇦",
    links: [
      {
        name: "Canadian Intellectual Property Office (CIPO) — Patents",
        description: "The official Canadian government office for patent applications. File new patents, search existing patents, and manage your intellectual property.",
        url: "https://ised-isde.canada.ca/site/canadian-intellectual-property-office/en/patents",
      },
      {
        name: "Canadian Patent Database",
        description: "Search the Canadian patent database to review existing patents and check for prior art before filing your application.",
        url: "https://ised-isde.canada.ca/cipo/patent-search/srch?lang=eng",
      },
      {
        name: "CIPO — How to File a Patent Application",
        description: "Step-by-step guide from the Canadian government on the patent application process, including what to prepare and how to file.",
        url: "https://ised-isde.canada.ca/site/canadian-intellectual-property-office/en/patents/how-file-patent-application",
      },
      {
        name: "CIPO — Patent Fees",
        description: "Complete fee schedule for patent applications, examinations, maintenance, and other services.",
        url: "https://ised-isde.canada.ca/site/canadian-intellectual-property-office/en/patents/patent-fees",
      },
      {
        name: "CIPO — Patent Guide",
        description: "Comprehensive guide explaining what patents are, what can be patented, and the lifecycle of a patent in Canada.",
        url: "https://ised-isde.canada.ca/site/canadian-intellectual-property-office/en/patents/patent-guide",
      },
    ],
  },
  {
    title: "United States",
    icon: "🇺🇸",
    links: [
      {
        name: "United States Patent and Trademark Office (USPTO) — Patents",
        description: "The official US government office for patent registration. File applications, search patents, and access inventor resources.",
        url: "https://www.uspto.gov/patents",
      },
      {
        name: "USPTO — Patent Full-Text and Image Database (PatFT)",
        description: "Search the full text of issued US patents to check for prior art and existing intellectual property.",
        url: "https://patft.uspto.gov/",
      },
      {
        name: "USPTO — Patent Center (File Online)",
        description: "File and manage patent applications online through the USPTO's electronic filing system.",
        url: "https://patentcenter.uspto.gov/",
      },
      {
        name: "USPTO — Patent Basics",
        description: "Educational resource covering the types of patents (utility, design, plant), what can be patented, and how the process works.",
        url: "https://www.uspto.gov/patents/basics",
      },
      {
        name: "USPTO — Patent Fees",
        description: "Current fee schedule for patent applications, search fees, examination fees, issue fees, and maintenance fees.",
        url: "https://www.uspto.gov/patents/fees",
      },
      {
        name: "USPTO — Inventors Assistance Center",
        description: "Free resources and support for independent inventors and small businesses navigating the patent process.",
        url: "https://www.uspto.gov/learning-and-resources/support-centers/inventors-assistance-center-iac",
      },
    ],
  },
];

export default function PatentRegistrations() {
  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <section className="py-24 relative">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" aria-hidden="true" />
          <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8 bg-primary" />
                <span className="text-primary font-medium tracking-wide uppercase text-sm">Founder Resources</span>
              </div>
              <h1 className="text-2xl sm:text-4xl md:text-6xl font-display font-bold mb-6" data-testid="text-patent-title">Patent Registrations</h1>
              <p className="text-xl text-muted-foreground max-w-2xl">
                Official government links for registering a patent in Canada and the United States.
              </p>
            </div>

            {sections.map((section) => (
              <div key={section.title} className="mb-16">
                <h2 className="text-2xl font-display font-bold mb-8 flex items-center gap-3">
                  <span className="text-3xl" aria-hidden="true">{section.icon}</span>
                  {section.title}
                </h2>

                <div className="space-y-3" role="list" aria-label={`${section.title} patent registration links`}>
                  {section.links.map((link) => (
                    <Card key={link.url} role="listitem" className="border-white/5 p-5 hover:border-primary/20 transition-all">
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-start gap-4 group"
                        data-testid={`link-pat-${link.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`}
                      >
                        <FileCheck size={18} className="text-primary shrink-0 mt-1" aria-hidden="true" />
                        <div className="flex-1">
                          <h4 className="font-semibold group-hover:text-primary transition-colors flex items-center gap-2">
                            {link.name}
                            <ExternalLink size={14} className="text-muted-foreground" aria-hidden="true" />
                            <span className="sr-only">(opens in new tab)</span>
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">{link.description}</p>
                        </div>
                      </a>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
